import { compare, type Cleaners } from '@/core/lib/compare'
import {
  dateMismatch,
  type FieldError,
  type FieldErrors,
  type FieldSource,
} from '@/core/lib/fieldError'
import { findMalformedDates, BEWILLIGUNG_DATE_PATHS, templatePaths } from '@/core/lib/validate'
import { GERMAN_MONTHS } from '@/core/lib/germanDate'
import { getAt } from '@/core/lib/path'
import { normalizeMassnahmeNummer } from '@/core/lib/massnahme'
import { bewilligungForm } from '../templates'
import type { FieldId } from '@/core/lib/result'
import type { BewilligungForm } from '../lib/extractBewilligung'

const s = (v: unknown) => (v == null ? '' : String(v))

function toCanonicalDate(value: unknown): string {
  const raw = s(value).trim()
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  const numeric = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(raw)
  if (numeric) return `${numeric[3]}-${numeric[2].padStart(2, '0')}-${numeric[1].padStart(2, '0')}`
  const long = /^(\d{1,2})\.?\s+([A-Za-zäöüÄÖÜ]+)\s+(\d{4})/.exec(raw)
  if (long) {
    const mm = GERMAN_MONTHS[long[2].toLowerCase()]
    if (mm) return `${long[3]}-${mm}-${long[1].padStart(2, '0')}`
  }
  return raw.toLowerCase()
}

function toCanonicalCount(value: unknown): string {
  return s(value).replace(/\D+/g, '')
}

function toCanonicalOrt(value: unknown): string {
  return s(value)
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

const BEWILLIGUNG_CLEANERS: Cleaners = {
  'Maßnahme.Beginn der Maßnahme': toCanonicalDate,
  'Maßnahme.Ende der Maßnahme': toCanonicalDate,
  'Maßnahme.Zeitlicher Umfang (max UE)': toCanonicalCount,
  'Maßnahme.Durchführungsort': toCanonicalOrt,
  'Maßnahme.Nummer der Maßnahme': normalizeMassnahmeNummer,
}

const VORNAME: FieldId = 'Teilnehmer.Vorname'
const NACHNAME: FieldId = 'Teilnehmer.Nachname'
const DURCHFUEHRUNGSORT: FieldId = 'Maßnahme.Durchführungsort'

export const KOSTENTRAEGER_FIELDS: readonly FieldId[] = templatePaths(bewilligungForm).filter((p) =>
  p.startsWith('Kostenträger.'),
)
const COMPARE_PATHS = templatePaths(bewilligungForm).filter(
  (p) => p !== VORNAME && !KOSTENTRAEGER_FIELDS.includes(p),
)

const INHALT_PAIR: readonly FieldId[] = [
  'Maßnahme.Maßnahmeinhalt (Gruppenmaßnahme)',
  'Maßnahme.Maßnahmeinhalt (Einzelmaßnahme)',
]
const label = (f: FieldId) => f.split('.').pop() ?? f

const SOURCE_BY_PATH: Record<FieldId, FieldSource> = {
  'Teilnehmer.Nachname': 'Stammdaten',
  'Maßnahme.Nummer der Maßnahme': 'Trägerbestätigung',
  'Maßnahme.Maßnahmeinhalt (Gruppenmaßnahme)': 'AVGS',
  'Maßnahme.Maßnahmeinhalt (Einzelmaßnahme)': 'AVGS',
  'Maßnahme.Durchführungsort': 'Organisation',
  'Maßnahme.Beginn der Maßnahme': 'Trägerbestätigung',
  'Maßnahme.Ende der Maßnahme': 'Trägerbestätigung',
  'Maßnahme.Zeitlicher Umfang (Kategorie)': 'AVGS',
  'Maßnahme.Zeitlicher Umfang (max UE)': 'AVGS',
}
const DATE_PATHS = new Set<FieldId>(BEWILLIGUNG_DATE_PATHS)

export type BewilligungCheck = {
  errors: FieldErrors
  mismatches: FieldId[]
  nameMismatch: boolean
  adoptable: FieldId[]
  ortMismatch: boolean
  notFound: string[]
}

export function checkBewilligung(
  extracted: BewilligungForm,
  reference: BewilligungForm,
): BewilligungCheck {
  const errors = new Map<FieldId, FieldError>()
  const mismatches: FieldId[] = []
  let ortMismatch = false
  const malformed = new Set(findMalformedDates(extracted, BEWILLIGUNG_DATE_PATHS))

  for (const { field, expected } of compare(extracted, reference, COMPARE_PATHS, BEWILLIGUNG_CLEANERS)) {
    if (s(getAt(extracted, field)).trim() === '') continue
    if (malformed.has(field)) continue
    if (expected.trim() === '') continue

    const source = SOURCE_BY_PATH[field] ?? 'AVGS'
    errors.set(
      field,
      DATE_PATHS.has(field) ? dateMismatch(source, expected) : { kind: 'mismatch', source, expected },
    )
    if (field === DURCHFUEHRUNGSORT) {
      ortMismatch = true
      continue
    }
    mismatches.push(field)
  }

  const nameMismatch = mismatches.includes(NACHNAME)
  const adoptable = mismatches.filter((f) => f !== NACHNAME)

  const isBlank = (f: FieldId) => s(getAt(extracted, f)).trim() === ''
  const refBlank = (f: FieldId) => s(getAt(reference, f)).trim() === ''
  const notFound: string[] = []
  for (const f of COMPARE_PATHS) {
    if (INHALT_PAIR.includes(f)) continue
    if (isBlank(f) && !refBlank(f)) {
      errors.set(f, { kind: 'missing' })
      notFound.push(label(f))
    }
  }
  if (INHALT_PAIR.every(isBlank) && !INHALT_PAIR.every(refBlank)) {
    for (const f of INHALT_PAIR) errors.set(f, { kind: 'missing' })
    notFound.push('Maßnahmeinhalt')
  }

  return {
    errors,
    mismatches,
    nameMismatch,
    adoptable,
    ortMismatch,
    notFound,
  }
}

export type KostentraegerCheck = {
  errors: FieldErrors
  missing: FieldId[]
}

export function checkKostentraeger(values: unknown): KostentraegerCheck {
  const errors = new Map<FieldId, FieldError>()
  const missing: FieldId[] = []
  for (const f of KOSTENTRAEGER_FIELDS) {
    if (s(getAt(values, f)).trim() === '') {
      errors.set(f, { kind: 'missing' })
      missing.push(f)
    }
  }
  return { errors, missing }
}
